/**
 * Calculates all totals for invoices. Included in the Invoice
 * service and includes the service for invoice item totals
 */
export function InvoiceTotalsService (
  bigDecimalApi,
  InvoiceItemTotals,
  sessionObjAPI
) {

  var InvoiceTotals = function (invoice) {
    this.company     = sessionObjAPI.company();
    this.hasGroupTax = false;
    this.info        = sessionObjAPI.currency();
    this.invoice     = invoice;
    this.itemTotals  = new InvoiceItemTotals(invoice);
  };

  var calculateMultiTax = function (taxRates, total, totalExcludingLabor, skipDisplay) {
    var tax = BigDecimal.ZERO;

    // If the value is less than 0, a discount is being applied
    // that is greater than the amount owed. Tax is zero.
    total = total < 0 ? BigDecimal.ZERO: total;
    totalExcludingLabor = totalExcludingLabor < 0 ? BigDecimal.ZERO: totalExcludingLabor;

    angular.forEach(taxRates, function (rate) {

      // If the rate has labor_gst_free, we want to calculate
      // tax based off the total excluding labor.
      rate.multi_tax_result = rate.labor_gst_free ?
        totalExcludingLabor.multiply(rate.tax_rate.movePointLeft(2)):
        total.multiply(rate.tax_rate.movePointLeft(2));

      rate.multi_tax_result_display = rate.multi_tax_result.setScale(4, BigDecimal.ROUND_HALF_UP).toString();
    });

    angular.forEach(taxRates, function (rate) {
      tax = tax.add(rate.multi_tax_result);
    });

    return tax;
  };

  var calculateSalesTax = function (taxRate, total) {
    return total.multiply(taxRate);
  };

  var getTaxRates = function (taxGroup) {
    var taxRates = taxGroup.tax_group_items_attributes;

    angular.forEach(taxRates, function (rate) {
      rate.tax_rate = bigDecimalApi.get(rate.tax_rate).setScale(2, BigDecimal.ROUND_HALF_UP);
    });

    return taxRates;
  };

  /**
   * Calculates the balance due for the whole invoice
   * @return {string} the balance due for the invoice
   */
  InvoiceTotals.prototype.balance = function () {
    var self = this;
    var total = bigDecimalApi.get(self.total()).setScale(2, BigDecimal.ROUND_HALF_UP);
    var deposit = bigDecimalApi.get(self.deposits());

    // If the invoice is close, return what is sent from the server
    if (self.closed()) {
      return self.invoice.balance_due || '0';
    }

    // If not, return the total
    return total.subtract(deposit).toString();
  };

  InvoiceTotals.prototype.calculateTaxByItems = function ( skips = {} ) {
    const self = this;

    let tax = bigDecimalApi.get();

    if ( !skips.freight ) {
      const freight = self.company.tax_freight
        ? bigDecimalApi.get(self.invoice.freight)
        : BigDecimal.ZERO;

      const taxRate = bigDecimalApi.get(self.company.sales_tax_rate).movePointLeft(2);

      /**
       * Tax handling for freight calculations:
       *
       * If the price already includes tax: GST = freight * tax_rate / (1 + tax_rate)
       * If the price excludes tax: GST = freight * tax_rate
       */
      const freightTax = self.info.price_includes_tax
        ? freight.multiply(taxRate)
          .divide(BigDecimal.ONE.add(taxRate))
        : freight.multiply(taxRate);

      tax = tax.add(freightTax.setScale(2, BigDecimal.prototype.ROUND_HALF_UP));
    }

    angular.forEach(self.invoice.invoice_items_attributes, ( item ) => {
      tax = tax.add(bigDecimalApi.get(self.itemTotals.tax(item)));
    });

    return tax;
  };

  /**
   * If the flag "calculate_tax_on_subtotal" is set, then tax needs to be calculated
   * at the subtotal level, not just the total of all the item's tax calculations.
   * This handles that form of calculations
   * @return {object} The Big Decimal value for the calculated tax
   */
  InvoiceTotals.prototype.calculateTaxBySubtotal = function (skips) {
    var self = this;
    var tax;
    var skips = skips || {};
    var subtotal = BigDecimal.ZERO;
    var subtotalExcludingLabor = BigDecimal.ZERO;
    var discount = bigDecimalApi.get(self.invoice.discount);
    var freight = self.company.tax_freight ? bigDecimalApi.get(self.invoice.freight): BigDecimal.ZERO;
    var tax;
    var taxRate = bigDecimalApi.get(self.company.sales_tax_rate).movePointLeft(2);
    var taxRates, totalDiscount, subtotalDiscount;

    if (self.company.use_multi_tax) {
      taxRates = getTaxRates(self.invoice.tax_group);
    }

    // loop through the items and get the total of them, without taxes
    angular.forEach(self.invoice.invoice_items_attributes, function (item) {
      var unit_price = BigDecimal.ZERO;
      var quantity = BigDecimal.ZERO;

      // We only need the items where tax is included
      if (!item.gst_free) {
        unit_price = bigDecimalApi.get(item.unit_price).setScale(2, BigDecimal.ROUND_HALF_UP);
        quantity = bigDecimalApi.get(item.quantity && item.quantity !== '-' ? item.quantity : '0').setScale(2, BigDecimal.ROUND_HALF_UP);
        subtotal = subtotal.add(unit_price.multiply(quantity).setScale(2, BigDecimal.ROUND_HALF_UP));

        // Need to keep track of the subtotal excluding labor
        // items for use in tax groups who have labor_gst_free
        // rates.
        subtotalExcludingLabor = item.product_type === 'J' ?
          subtotalExcludingLabor:
          subtotalExcludingLabor.add(unit_price.multiply(quantity).setScale(2, BigDecimal.ROUND_HALF_UP));
      }

    });

    // Dont forget the freight amount
    if (!skips.freight) {
      subtotal               = subtotal.add(freight);
      subtotalExcludingLabor = subtotalExcludingLabor.add(freight);
    }

    // Now let's calculate the tax based on the subtotal of items

    // If the price includes tax, extract the tax from the total
    if (self.info.price_includes_tax) {
      tax = subtotal.subtract(
        subtotal.setScale(4, BigDecimal.ROUND_HALF_UP)
        .divide(
          BigDecimal.ONE.add(taxRate)
          .setScale(4, BigDecimal.ROUND_HALF_UP)
        )
      );

      tax = tax.setScale(2, BigDecimal.ROUND_HALF_UP);

    }

    // If not, calculate the tax based on the tax rate
    else {

      //if there is a discount, apply it;
      if (!discount.isZero() && !skips.discount) {
        totalDiscount          = bigDecimalApi.get(self.totalDiscount());
        subtotalDiscount       = bigDecimalApi.get(self.subtotalDiscount(totalDiscount.toString()));
        subtotal               = subtotal.subtract(subtotalDiscount);
        subtotalExcludingLabor = subtotalExcludingLabor.subtract(subtotalDiscount);
      }

      // Calculate tax based on use_multi_tax
      tax = self.company.use_multi_tax ?
        calculateMultiTax(taxRates, subtotal, subtotalExcludingLabor, (skips.discount && skips.freight)):
        calculateSalesTax(taxRate, subtotal);
    }

    return tax;
  };

  /**
   * Determines whether or not the invoice is closed based on status
   * @return {boolean} true if closed, false if open
   */
  InvoiceTotals.prototype.closed = function () {
    var self = this;

    return self.invoice.invoice_status !== 'O';
  };

  /**
   * If the invoice has deposits, calculate
   * the total number of deposits.
   * @return {string} The total amount of deposits
   */
  InvoiceTotals.prototype.deposits = function() {
    var self = this;
    var total = BigDecimal.ZERO;

    if (self.closed()) {
      return self.invoice.deposits_total || '0.00';
    }

    angular.forEach(self.invoice.customer_deposits_attributes, function (deposit) {
      var amount = bigDecimalApi.get(deposit.amount);

      total = total.add(amount);
    });

    return total.toString();
  };

  /**
   * Determines if the invoice discount type matches
   * the specified type
   * @param  {string} type The type of discount being checked
   * @return {boolean}     Is the invoice discount type matches the specified type
   */
  InvoiceTotals.prototype.discountType = function(type) {
    const self    = this;
    const typeMap = {
      cash      : 'C',
      itemized  : 'I',
      percentage: 'P'
    };

    return typeMap[type] === self.invoice.discount_type;
  };

  /**
   * Calculates the subtotal for the invoice
   * when price_includes_tax is on
   * accounts for freight being considered a line item
   * so this amount is different than subtotal (which excludes freight,
   * necessary for earlier calculations such as on discounts)
   * @return {string} The subtotal amount
   */
  InvoiceTotals.prototype.includesTaxSubtotal = function() {
    const self    = this;
    const freight = bigDecimalApi.get(self.invoice.freight).setScale(2, BigDecimal.ROUND_HALF_UP);

    let total        = BigDecimal.ZERO;
    let taxableTotal = BigDecimal.ZERO;

    angular.forEach(self.invoice.invoice_items_attributes, function (item) {
      const itemTotal = bigDecimalApi.get( self.itemTotals.total(item) );

      total = total.add(itemTotal);

      if ( !item.gst_free && !self.invoice.tax_free ) {
        taxableTotal = taxableTotal.add(itemTotal);
      }
    });

    const unTaxableTotal  = total.subtract(taxableTotal);
    taxableTotal          = taxableTotal.add(freight);
    const taxableSubtotal = taxableTotal.subtract(self.calculateTaxByItems());
    const subtotal        = taxableSubtotal.add(unTaxableTotal).subtract(freight);

    return subtotal
      ? subtotal.toString()
      : '0';
  };

  /**
   * Determines if the specified value is equal to zero.
   * Sometimes values are stores as the string "0.00".
   * In JavaScript, this will not show as 0, so we need
   * parse and check.
   * @param  {variable} value The value to check
   * @return {boolean}       true if zero, false if not
   */
  InvoiceTotals.prototype.isZero = function(value) {
    var self = this;

    value = bigDecimalApi.get(value);

    return value.isZero();

  };

  /**
   * Determines the amount that has been paid.
   * @return {string} the total minus the balance
   */
  InvoiceTotals.prototype.paid = function() {
    var self = this;
    var total = bigDecimalApi.get(self.total());
    var balance = bigDecimalApi.get(self.balance());

    return total.subtract(balance).toString();
  };

  /**
   * If the round_total flag is set, calculate the
   * rounding value to display it
   * @return {string} the rounded amount
   */
  InvoiceTotals.prototype.rounding = function() {
    var self = this;
    var total, tax, freight, roundedTotal;
    var subtotal = self.info.price_includes_tax ? bigDecimalApi.get(self.includesTaxSubtotal()) : bigDecimalApi.get(self.subtotal());

    if (!self.info.round_total) {
      return '0';
    }
    else if (self.closed()) {
      return self.invoice.rounding;
    }
    else {
      tax = self.invoice.tax_free ? BigDecimal.ZERO: bigDecimalApi.get(self.tax({ discount: self.hasGroupTax })).setScale(2, BigDecimal.ROUND_HALF_UP);
      freight = bigDecimalApi.get(self.invoice.freight).setScale(2, BigDecimal.ROUND_HALF_UP);
      roundedTotal = bigDecimalApi.get(bigDecimalApi.round(subtotal.add(tax).add(freight).toString()));

      return (subtotal.add(tax).add(freight)).subtract(roundedTotal).negate().toString();
    }
  };

  InvoiceTotals.prototype.subtotal = function(skips) {
    var self     = this;
    var subtotal = BigDecimal.ZERO;
    var total    = BigDecimal.ZERO;
    var discount = bigDecimalApi.get(self.invoice.discount);
    var totalDiscount, subtotalDiscount;

    skips = skips || {};

    // If the invoice is closed, no need to do any calculations
    if (self.closed()) {
      return self.invoice.subtotal || '0';
    }

    // if the calculate tax flag is set, we need to get the tax out on the invoice level
    else if (self.info.price_includes_tax && !self.invoice.tax_free) {
      angular.forEach(self.invoice.invoice_items_attributes, item => {
        const itemTotal = bigDecimalApi.get( self.itemTotals.total(item) );

        total = total.add(itemTotal);
      });

      subtotal = total.subtract(self.calculateTaxByItems({ freight: true }));
    }

    // If not, just add up each items total before taxes
    else {
      angular.forEach(self.invoice.invoice_items_attributes, function (item){
        var unit_price = bigDecimalApi.get(item.unit_price);
        var quantity = bigDecimalApi.get( item.quantity && item.quantity !== '-' ? item.quantity : '0' );
        subtotal = subtotal.add( unit_price.multiply(quantity).setScale(2, BigDecimal.ROUND_HALF_UP) );
      });
    }

    // if there is a discount, apply it;
    if (!discount.isZero() && !self.info.price_includes_tax && !skips.discount) {
      totalDiscount    = bigDecimalApi.get(self.totalDiscount());
      subtotalDiscount = bigDecimalApi.get(self.subtotalDiscount(totalDiscount.toString()));
      subtotal         = subtotal.subtract(subtotalDiscount);
    }

    return subtotal ? subtotal.toString(): '0';
  };

  /**
   * Determines just the amount being discounted from
   * the subtotal.
   * @return {string} the amount being discounted from subtotal
   */
  InvoiceTotals.prototype.subtotalDiscount = function(totalDiscount) {
    var self = this;
    var taxDiscount;

    // If the total was not passed then we need to calculate it
    totalDiscount = bigDecimalApi.get(totalDiscount || self.totalDiscount());

    taxDiscount = bigDecimalApi.get(self.taxDiscount(totalDiscount.toString()));

    // if discount is applied to subtotal (price_includes_tax is false),
    // return the total discount.
    return self.info.discount_includes_tax ?
      totalDiscount.subtract(taxDiscount).setScale(2, BigDecimal.ROUND_HALF_UP).toString():
      totalDiscount.toString();
  };

  /**
   * Calculates the tax for the whole invoice
   * @param {Object} skips An object containing any calculations that need to be skipped for whatever reason
   * @return {string} The total tax for the invoice
   */
  InvoiceTotals.prototype.tax = function (skips) {
    var self = this;
    var skips = skips || {};
    var calculatedTaxRate = BigDecimal.ZERO;
    var tax = BigDecimal.ZERO;
    var freight = self.company.tax_freight ? bigDecimalApi.get(self.invoice.freight): BigDecimal.ZERO;
    var taxRate = bigDecimalApi.get(self.company.sales_tax_rate).movePointLeft(2);
    var discount = bigDecimalApi.get(self.invoice.discount);
    var taxRates, totalDiscount, taxDiscount;

    // don't include tax on internal invoices
    if (self.type('internal')) {
      return BigDecimal.ZERO.toString();
    }

    if (self.company.use_multi_tax) {
      taxRates = getTaxRates(self.invoice.tax_group);
    }

    // If the invoice is closed, return the amount from the server
    if (self.closed()) {
      return self.invoice.gst;
    }

    // If the customer is tax free, return 0
    else if (self.invoice.tax_free) {
      return tax.toString();
    }

    else if (!self.info.price_includes_tax && self.company.has_xero) {
      tax = self.calculateTaxByItems(skips);

      // The next if block is for handling discounts with Xero
      // users. First we need to get our calculatedTaxRate,
      // described below. Then subtract from the tax calculated
      // above the applied discount.

      // set the calculated tax rate. For non multi-tax users, this
      // is just the normal taxRate. For multi-tax users, we add up
      // each rate in the tax group. This will be used to calculate
      // the discount from tax, if applicable.
      if (self.company.use_multi_tax) {
        angular.forEach(taxRates, function (rate) {
          calculatedTaxRate = calculatedTaxRate.add(rate.tax_rate.movePointLeft(2));
        });
      } else {
        calculatedTaxRate = calculatedTaxRate.add(taxRate);
      }

      // apply discount to the xero-specific calculated tax.
      if ( !discount.isZero() ) {

        // discount is cash
        if (self.invoice.discount_type === 'C') {
            var taxDiscount = bigDecimalApi.get(self.taxDiscount(discount.toString()));
            tax = tax.subtract(taxDiscount.setScale(2, BigDecimal.ROUND_HALF_UP));
        }

        // discount is a percentage
        else {
          if (!skips.discount) {
            tax = tax.subtract(tax.multiply(discount.movePointLeft(2)).setScale(2, BigDecimal.ROUND_HALF_UP));
          }
        }

        //make sure tax isn't negative
        tax = tax < 0 ? BigDecimal.ZERO: tax;
      }

      // The only reason for calling this is
      // to set each tax rate's multi_tax_result_display
      // property. We don't do anything with
      // the returned tax value because taxes are
      // calculated differently for Xero users.
      self.calculateTaxBySubtotal(skips);
    }

    else {
      tax = !self.company.use_multi_tax && self.hasGroupTax
      ? self.calculateTaxByItems(skips)
      : self.calculateTaxBySubtotal(skips);
    }

    // If there is no discount or if the discount is applied
    // to the subtotal (pretax, based on price_includes_tax),
    // just return what we have so far.
    if (discount.isZero() || skips.discount || !self.info.price_includes_tax) {
      return tax.setScale(2, BigDecimal.ROUND_HALF_UP).toString();
    }

    // Unsure if this needs any changing for multi tax. The discount
    // should be applied to the total, meaning price_includes_tax is
    // true. That would mean use_multi_tax is false.
    totalDiscount = bigDecimalApi.get(self.totalDiscount());
    taxDiscount = bigDecimalApi.get(self.taxDiscount(totalDiscount.toString()));

    return tax.subtract(taxDiscount).setScale(2, BigDecimal.ROUND_HALF_UP).toString();
  };

  /**
   * Determines just the amount being discounted from
   * the tax.
   * @return {string} the amount being discounted from tax
   */
  InvoiceTotals.prototype.taxDiscount = function(totalDiscount) {
    var self = this;
    var taxRate = bigDecimalApi.get(self.company.sales_tax_rate).movePointLeft(2);

    totalDiscount = bigDecimalApi.get(totalDiscount).setScale(4, BigDecimal.ROUND_HALF_UP);

    return self.info.discount_includes_tax ? totalDiscount.subtract(totalDiscount.divide(BigDecimal.ONE.add(taxRate))).toString() : totalDiscount.multiply(taxRate).toString() ;
  };

  /**
   * Calculate the total of the invoice
   * @return {string} The total for the invoice
   */
  InvoiceTotals.prototype.total = function() {
    var self = this;

    var subtotal = self.info.price_includes_tax ? bigDecimalApi.get(self.includesTaxSubtotal()) : bigDecimalApi.get(self.subtotal());
    var tax      = self.invoice.tax_free ? BigDecimal.ZERO: bigDecimalApi.get(self.tax({ discount: self.hasGroupTax })).setScale(2, BigDecimal.ROUND_HALF_UP);
    var freight  = bigDecimalApi.get(self.invoice.freight).setScale(2, BigDecimal.ROUND_HALF_UP);
    var discount = bigDecimalApi.get(self.invoice.discount);
    var total    = BigDecimal.ZERO;
    var totalDiscount, subtotalDiscount, taxDiscount;

    // If the invoice is closed, return the amount from the server
    if (self.closed()) {
      return self.invoice.total || '0';
    }

    // if there is a discount, apply it;
    if (!discount.isZero() && self.info.price_includes_tax) {
      totalDiscount    = bigDecimalApi.get(self.totalDiscount());
      subtotalDiscount = bigDecimalApi.get(self.subtotalDiscount(totalDiscount.toString()));
      subtotal         = subtotal.subtract(subtotalDiscount);
    }

    // Add any terminal surcharge
    var surcharge = bigDecimalApi.get(self.invoice.tillpayments_surcharge_amount);

    // if the customer is tax_free then return the subtotal with the freight included as well
    if (this.invoice.tax_free == true) {
      return subtotal.add(freight).add(surcharge).toString();
    }

    // Add tax to subtotal
    total = subtotal.add(tax);
    total = total.add(surcharge);

    // Round the total if the flag is set
    if (self.info.round_total) {
      return bigDecimalApi.round(total.add(freight).toString());
    }

    // Dont forget the tax and the freight amount
    return total.add(freight).toString();
  };

  /**
   * Determines the total amount discounted if
   * there is a discount applied
   * @return {string} the total amount discounted
   */
  InvoiceTotals.prototype.totalDiscount = function() {
    const self   = this;
    const discount = bigDecimalApi.get(self.invoice.discount).setScale(2, BigDecimal.ROUND_HALF_UP);
    let subtotal, tax, total;

    // if the invoice discount type is cash or
    // itemized, or if the discount is zero,
    // return discount.
    if (discount.isZero() || self.discountType('cash') || self.discountType('itemized')) {
      return discount.toString();
    }

    // if the discount type is percentage, calculate
    // and return the discount.
    else if (self.discountType('percentage')) {
      subtotal = bigDecimalApi.get(self.subtotal({discount: true}));
      tax      = bigDecimalApi.get(self.tax({discount: true, freight: true}));
      total    = self.info.discount_includes_tax ? subtotal.add(tax) : subtotal;

      return total
        .multiply(discount.movePointLeft(2))
        .setScale(2, BigDecimal.ROUND_HALF_UP)
        .toString();
    }

    return BigDecimal.ZERO.toString();
  };

  /** Calculates the total itemized discount by
   * iterating through each product and adding
   * together each product's discount_total.
   * @return {BigDecimal} the total itemized discount.
   */
  InvoiceTotals.prototype.totalItemizedDiscount = function () {
    const self   = this;
    let discount = bigDecimalApi.get('0').setScale(2, BigDecimal.ROUND_HALF_UP);
    self.invoice.invoice_items_attributes.forEach(item => {
      item.discount_total = self.itemTotals.discountTotal(item);
      // add item's discount total calculated
      // to reflect quantity.
      discount = discount.add(
        bigDecimalApi.get(item.discount_total).setScale(2, BigDecimal.ROUND_HALF_UP)
      );
    });
    return discount;
  };

  /**
   * Determines if the invoice matches the specified
   * type
   * @param  {string} type    The type of invoice being checked
   * @return {boolean}        Is the invoice the specified type
   */
  InvoiceTotals.prototype.type = function (type) {
    var self = this;
    var typeMap = {
      credit  : 'C',
      quote   : 'Q',
      invoice : 'I',
      internal: 'U'
    };

    return typeMap[type] === self.invoice.invoice_type;
  };

  return InvoiceTotals;
}